import { Component, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { FlowDefinition } from '@neuroedge/shared';
import { FlowStepsPreview } from '../../automation-flows/FlowStepsPreview';

/** A flow that can't be drawn (e.g. an older or hand-edited definition) only hides its preview. */
class PreviewBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? (
      <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
        This flow's steps can't be previewed here - open the flow to see them.
      </span>
    ) : (
      this.props.children
    );
  }
}

/**
 * The steps of the flow a "Start a guided conversation" step runs, under its
 * editor in the visual builder's side panel - so you can see the whole
 * conversation without leaving the automation.
 */
export function FlowPreviewPanel({
  name,
  definition,
  loading,
  openHref,
}: {
  name: string;
  definition: FlowDefinition | undefined;
  loading?: boolean;
  /** Where "Open this flow" goes (its editor, or the flows page). */
  openHref?: string;
}) {
  return (
    <div
      aria-label="Flow preview"
      role="region"
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        borderTop: '1px solid var(--color-border)',
        paddingTop: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        <strong>What "{name}" does</strong>
        <span style={{ flex: 1 }} />
        {openHref ? (
          <Link to={openHref} style={{ fontSize: 'var(--font-size-sm)' }}>
            Open this flow →
          </Link>
        ) : null}
      </div>
      {definition ? (
        <PreviewBoundary>
          <FlowStepsPreview definition={definition} />
        </PreviewBoundary>
      ) : (
        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {loading ? 'Loading the flow…' : 'This flow could not be loaded.'}
        </span>
      )}
    </div>
  );
}
